import { useEffect, useState } from 'react';
import styles from './styles.module.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import { TiArrowRightOutline } from 'react-icons/ti';
import { QuestionType, QuestionTypeResponse } from '@robotical/analytics-gatherer';
import { answerQuestion, fetchQuestionOfTheWeek } from '../../db/news-section-db';
import { getUrlParams } from '../../utils/url-params';
import { FaThumbsUp } from 'react-icons/fa';

type Props = {
    visibility: "hidden" | "visible";
}

const QuestionOfTheWeekSection = ({ visibility }: Props) => {
    const [isLoading, setLoading] = useState(true);
    const [question, setQuestion] = useState<QuestionType | null>(null);
    const [hasAnswered, setHasAnswered] = useState(false);
    const [answer, setAnswer] = useState<string | null>(null);

    useEffect(() => {
        const asyncFunc = async () => {
            setLoading(true);
            const question = await fetchQuestionOfTheWeek();
            const params = getUrlParams();
            const hasAnswered = hasThisUserAnswerThisQuestion(question, params.serialNumber, params.deviceId);
            if (Object.keys(question).length) {
                setQuestion(Object.values(question)[0]);
            }
            setHasAnswered(hasAnswered);
            setLoading(false);
        };

        asyncFunc();
    }, []);

    const onAnswerQuestion = async () => {
        if (!answer || !question) return;
        setLoading(true);
        const params = getUrlParams();
        const questionId = question.id;
        const questionText = question.question;
        const serialNo = params.serialNumber;
        const deviceId = params.deviceId;
        await answerQuestion(questionId, questionText, answer, serialNo, deviceId);
        setLoading(false);
        setAnswer(null);
        setHasAnswered(true);
    };

    if (!question && !isLoading) return <div className={styles.outerContainer} style={{visibility: visibility}}></div>;

    return (
        <div className={styles.outerContainer} style={{visibility: visibility}}>
            {isLoading ? <LoadingSpinner width='40px' /> : <div className={styles.newsContainer}>
                <div className={styles.questionContainer}>
                    <div className={styles.questionTitle}>{question?.question}</div>
                    <div className={styles.questionBody}>
                        {!hasAnswered ? <>
                            <textarea className={styles.questionTextArea} placeholder='Type your answer here...' value={answer || ''} onChange={(e) => setAnswer(e.target.value)} />
                            <div className={styles.button} onClick={onAnswerQuestion}><TiArrowRightOutline size={30} /></div>
                        </> : <div className={styles.questionAnsweredContainer}><FaThumbsUp size={20} style={{ transform: 'scaleX(-1)' }} /> Thank you for letting us know! <FaThumbsUp size={20} /></div>}
                    </div>
                </div>
            </div>}
        </div>
    );
};

export default QuestionOfTheWeekSection;

const hasThisUserAnswerThisQuestion = (questions: QuestionTypeResponse, thisSerialNo: string | null, thisDeviceId: string | null): boolean => {
    let hasAnswered = true;
    Object.keys(questions).forEach((questionId) => {
        const question = questions[questionId];
        if (question.whoHaveAnswered) {
            Object.keys(question.whoHaveAnswered).forEach((whoHaveAnsKey) => {
                const whoHaveAns = question.whoHaveAnswered![whoHaveAnsKey];
                /**
                 * @explaination 
                 * If the there is no serialNo but there is a device id then we only want to decide by device id (in the case that the user hasn't connected their robot yet)
                 * If there is a serialNo but no device id then we only want to decide by serialNo (this shouldn't really happen since all devices should have a device id)
                 * If there is both a serialNo and a device id then we want to decide by both
                 * If there is neither a serialNo or a device id then we don't want to decide at all (this shouldn't really happen since all devices should have a device id)
                 */
                if (thisSerialNo && thisDeviceId) {
                    if (whoHaveAns.serialNo !== thisSerialNo && whoHaveAns.deviceId !== thisDeviceId) {
                        return false;
                    }
                } else if (thisSerialNo && !thisDeviceId) {
                    if (whoHaveAns.serialNo !== thisSerialNo) {
                        return false;
                    }
                } else if (!thisSerialNo && thisDeviceId) {
                    if (whoHaveAns.deviceId !== thisDeviceId) {
                        return false;
                    }
                } else {
                    return false;
                }
            });
        } else {
            return false;
        }
    });
    return hasAnswered;
};
